/**
 * DrawingContextMenu
 *
 * Floating context menu that appears during cable path drawing.
 * Provides options to add a junction box or end the path.
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Square, CircleStop } from 'lucide-react';

interface DrawingContextMenuProps {
  /** Screen position for the menu (in screen/viewport coordinates) */
  screenPosition: { x: number; y: number };
  /** Called when user wants to add a junction box at current point */
  onAddBox: () => void;
  /** Called when user wants to end the path at current point */
  onEnd: () => void;
  /** Whether the menu is currently processing an action */
  isProcessing?: boolean;
}

export function DrawingContextMenu({
  screenPosition,
  onAddBox,
  onEnd,
  isProcessing = false,
}: DrawingContextMenuProps) {
  return (
    <div
      style={{
        position: 'fixed',
        left: screenPosition.x + 12,
        top: screenPosition.y - 44,
        zIndex: 1001,
      }}
      className="flex gap-1 bg-white rounded-lg shadow-lg border border-border p-1 animate-in fade-in-0 zoom-in-95 duration-100"
      data-context-menu="true"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Button
        size="sm"
        variant="outline"
        onClick={onAddBox}
        disabled={isProcessing}
        className="h-8 px-2 text-xs font-medium gap-1"
        title="Add junction box and continue drawing"
      >
        <Square className="h-3 w-3" />
        + Box
      </Button>
      <Button
        size="sm"
        variant="default"
        onClick={onEnd}
        disabled={isProcessing}
        className="h-8 px-3 text-xs font-medium"
        title="End path here"
      >
        <CircleStop className="h-3 w-3 mr-1" />
        End
      </Button>
    </div>
  );
}
